import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Card → detail (library, Foundations / Motion: "travels from where it came
 * from"). A dialog opened from a card grows out of the card's rect and, while
 * that card is still on screen, closes back into it. It moves by a translate,
 * one uniform scale and a clip to the card's shape, so nothing stretches. The
 * motion itself is CSS (`.co-from-card` in index.css); this writes where from.
 */

/** The card's corner radius, which the clip starts from. */
const CARD_RADIUS = 12;

function onScreen(rect: DOMRect): boolean {
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
}

/**
 * Aims the dialog at the card: scaled to fit inside the card and centred on it,
 * with the card's corner radius, so it starts small and grows out of the card. With no card on screen it falls back to
 * the plain dialog's rise (10px, 97%), and returns false.
 */
export function aimAtCard(dialog: HTMLElement, card: Element | null | undefined, leaving = false): boolean {
  // The dialog's box at rest. It may already carry an earlier aim's transform
  // (refs run twice in development, and again on a reopen), which would shrink
  // it and aim the next one at the wrong size and place, so undo the transform
  // (a translate and one uniform scale from the top left corner) rather than
  // trust the rect as it is.
  const shown = dialog.getBoundingClientRect();
  const m = new DOMMatrix(getComputedStyle(dialog).transform);
  const k = m.a || 1;
  const to = { left: shown.left - m.e, top: shown.top - m.f, width: shown.width / k, height: shown.height / k };
  const from = card?.getBoundingClientRect();
  const set = (name: string, value: string) => dialog.style.setProperty(name, value);
  if (!from || !onScreen(from) || !to.width || !to.height) {
    set("--card-x", "0px");
    set("--card-y", leaving ? "0px" : "10px");
    set("--card-s", "0.97");
    for (const side of ["t", "r", "b", "l"]) set(`--card-c${side}`, "0px");
    set("--card-r", "16px");
    set("--card-o", "0");
    return false;
  }
  // Scaled to sit inside the card (not to cover it), so the dialog starts small
  // and grows out of the card, centred on it, rather than starting larger than
  // the card and being cut to its shape.
  const scale = Math.min(from.width / to.width, from.height / to.height);
  set("--card-x", `${from.left + (from.width - to.width * scale) / 2 - to.left}px`);
  set("--card-y", `${from.top + (from.height - to.height * scale) / 2 - to.top}px`);
  set("--card-s", String(scale));
  for (const side of ["t", "r", "b", "l"]) set(`--card-c${side}`, "0px");
  set("--card-r", `${CARD_RADIUS / scale}px`);
  set("--card-o", "0.4");
  return true;
}

/**
 * For a dialog element that carries `.co-from-card`: the ref to put on it, which
 * aims it at the card when it mounts, and aims it again as it closes, at the
 * card showing then (J and K may have moved on). `card` is read at those two
 * moments only.
 */
export function useGrowFromCard(card: (() => Element | null) | undefined, isOpen: boolean) {
  const node = useRef<HTMLElement | null>(null);
  const cardRef = useRef(card);
  useLayoutEffect(() => {
    cardRef.current = card;
  });
  useLayoutEffect(() => {
    if (!isOpen && node.current && cardRef.current) aimAtCard(node.current, cardRef.current(), true);
  }, [isOpen]);
  return useCallback((element: HTMLElement | null) => {
    node.current = element;
    if (element && cardRef.current) aimAtCard(element, cardRef.current());
  }, []);
}
